import React from "react";
import { Circle, CircleOutlined } from "@mui/icons-material";
import { SystemStyleObject } from "@mui/system";
import { Theme, Tooltip } from "@mui/material";
import { CandidateStatusColor } from "../../Enums/CandidateStatusColor";

type IconSize = "small" | "medium";

interface CandidateStatusIconProps {
  statusColor: string;
  size?: IconSize;
  tooltip?: string;
  onClick?: (event: React.MouseEvent<SVGSVGElement>) => void;
}

type ColorSx = (theme: Theme) => SystemStyleObject<Theme>;

interface ColorSpec {
  isOutlined: boolean;
  color: (theme: Theme) => string;
}

const colorSpecByStatus: Record<CandidateStatusColor, ColorSpec> = {
  [CandidateStatusColor.White]: {
    isOutlined: true,
    color: (theme) => theme.palette.grey[400],
  },
  [CandidateStatusColor.Green]: {
    isOutlined: false,
    color: (theme) => theme.palette.candidateStatus.green,
  },
  [CandidateStatusColor.Blue]: {
    isOutlined: false,
    color: (theme) => theme.palette.candidateStatus.blue,
  },
  [CandidateStatusColor.Purple]: {
    isOutlined: false,
    color: (theme) => theme.palette.candidateStatus.purple,
  },
  [CandidateStatusColor.Yellow]: {
    isOutlined: false,
    color: (theme) => theme.palette.candidateStatus.yellow,
  },
  [CandidateStatusColor.Red]: {
    isOutlined: false,
    color: (theme) => theme.palette.candidateStatus.red,
  },
};

const resolveSpec = (statusColor: string): ColorSpec => {
  const normalized = (Object.values(CandidateStatusColor) as string[]).includes(statusColor)
    ? (statusColor as CandidateStatusColor)
    : CandidateStatusColor.White;
  return colorSpecByStatus[normalized];
};

export const CandidateStatusIcon: React.FC<CandidateStatusIconProps> = ({
  statusColor,
  size = "small",
  tooltip,
  onClick,
}) => {
  const spec = resolveSpec(statusColor);
  const IconComponent = spec.isOutlined ? CircleOutlined : Circle;
  const sx: ColorSx = (theme) => ({
    color: spec.color(theme),
    ...(onClick ? { cursor: "pointer" } : {}),
  });

  const icon = (
    <IconComponent
      data-test="candidate-status-icon"
      data-status-color={statusColor}
      fontSize={size}
      sx={sx}
      onClick={onClick}
    />
  );

  if (!tooltip) {
    return icon;
  }

  return (
    <Tooltip arrow title={tooltip}>
      <span style={{ display: "inline-flex" }}>{icon}</span>
    </Tooltip>
  );
};
